import React, { useState } from 'react';
import { useWBL } from '../../context/WBLContext';
import { Settings, Plus, Trash2, Calendar, RefreshCw, Globe } from 'lucide-react';
import { useInAppDialog } from '../Common/InAppDialog';

export const ConfigTabContent: React.FC = () => {
  const { systemConfig, updateSystemConfig, tenants, globalBranding, updateGlobalBranding, updateTenantBranding } = useWBL();
  const { showAlert, showToast, showConfirm } = useInAppDialog();
  const [newTerm, setNewTerm] = useState('');

  const handleAddTerm = () => {
    if (!newTerm.trim()) return;
    if (systemConfig.availableTerms?.includes(newTerm.trim())) {
      showAlert({
        title: "Duplicate Term",
        message: "Academic term already exists in configuration.",
        type: "warning"
      });
      return;
    }
    const updatedTerms = [...(systemConfig.availableTerms || []), newTerm.trim()];
    updateSystemConfig({ availableTerms: updatedTerms });
    setNewTerm('');
    showToast({
      title: "Term Added",
      message: `Added "${newTerm.trim()}" to academic terms.`,
      type: "success"
    });
  };

  const handleDeleteTerm = async (term: string) => {
    if (term === systemConfig.currentTerm) {
      showAlert({
        title: "Active Term",
        message: "Cannot delete the current active term. Change the current term first.",
        type: "error"
      });
      return;
    }
    const confirmed = await showConfirm({
      title: "Remove Academic Term",
      message: `Are you sure you want to remove "${term}" from available terms?`,
      confirmText: "Remove Term",
      type: "danger"
    });
    if (confirmed) {
      const updatedTerms = (systemConfig.availableTerms || []).filter(t => t !== term);
      updateSystemConfig({ availableTerms: updatedTerms });
      showToast({
        title: "Term Removed",
        message: `Removed "${term}" from academic terms.`,
        type: "info"
      });
    }
  };

  const handleRestoreDefaultTerms = async () => {
    const confirmed = await showConfirm({
      title: "Restore Default Terms",
      message: "Are you sure you want to restore the default academic terms (Fall 2026, Spring 2027, Summer 2027, Fall 2027)?",
      confirmText: "Restore Defaults",
      type: "warning"
    });
    if (confirmed) {
      const defaultTerms = ['Fall 2026', 'Spring 2027', 'Summer 2027', 'Fall 2027'];
      await updateSystemConfig({
        availableTerms: defaultTerms,
        currentTerm: 'Fall 2026'
      });
      showToast({
        title: "Terms Restored",
        message: "Academic terms restored to defaults.",
        type: "success"
      });
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-lg p-6 space-y-8">
      <div className="flex items-center justify-between border-b border-slate-100 pb-5">
        <h2 className="text-xl font-black text-[#011d3c] tracking-tight uppercase flex items-center space-x-2">
          <Settings className="w-6 h-6 text-[#0094c8]" />
          <span>System Configuration</span>
        </h2>
      </div>

      <div className="space-y-6">
        {/* Term Configuration Section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-[#0094c8]" />
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Academic Term Management</h3>
            </div>
            <button
              onClick={handleRestoreDefaultTerms}
              className="flex items-center space-x-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-all"
              title="Reset academic terms list to standard default terms"
            >
              <RefreshCw className="w-3.5 h-3.5 text-[#0094c8]" />
              <span>Restore Default Terms</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current Term Selector */}
            <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Active System Term</h4>
                <p className="text-[10px] text-slate-500 font-medium mt-1 leading-relaxed">
                  Select the term that will be used across the portal for applications and placements.
                </p>
              </div>
              <select
                value={systemConfig.currentTerm || 'Fall 2026'}
                onChange={(e) => updateSystemConfig({ currentTerm: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-[#011d3c] outline-none focus:ring-2 focus:ring-[#0094c8]/20 transition-all"
              >
                {(systemConfig.availableTerms || ['Fall 2026', 'Spring 2027', 'Summer 2027', 'Fall 2027']).map(term => (
                  <option key={term} value={term}>{term}</option>
                ))}
              </select>
            </div>

            {/* Term List Management */}
            <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Manage Term Registry</h4>
                <p className="text-[10px] text-slate-500 font-medium mt-1 leading-relaxed">
                  Add or remove terms from the available selection list.
                </p>
              </div>

              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newTerm}
                  onChange={(e) => setNewTerm(e.target.value)}
                  placeholder="e.g. Winter 2027"
                  className="flex-1 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium outline-none"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTerm()}
                />
                <button
                  onClick={handleAddTerm}
                  className="p-2 bg-[#0094c8] text-white rounded-xl hover:bg-[#007ba8] transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              <div className="max-h-40 overflow-y-auto space-y-1 pr-2 scrollbar-thin scrollbar-thumb-slate-200">
                {(systemConfig.availableTerms || ['Fall 2026', 'Spring 2027', 'Summer 2027', 'Fall 2027']).map(term => (
                  <div key={term} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-100 group">
                    <span className="text-xs font-bold text-slate-700">{term}</span>
                    <button
                      onClick={() => handleDeleteTerm(term)}
                      className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-all opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="h-px bg-slate-100 w-full" />

        {/* Global Settings Section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between p-5 bg-[#011d3c]/5 rounded-2xl border border-[#011d3c]/10">
            <div className="max-w-[80%]">
              <h3 className="text-sm font-black text-[#011d3c] uppercase tracking-wider">Multi-tenant Environment</h3>
              <p className="text-[10px] text-slate-500 font-medium mt-1 leading-relaxed">
                {systemConfig.multiTenantEnabled 
                  ? 'Active: The system is operating in multi-region mode. Homepage will display a regional selection grid.' 
                  : 'Inactive: The system is running in single-region mode. Users will be directed to the default region below.'}
              </p>
            </div>
            <button
              onClick={() => updateSystemConfig({ multiTenantEnabled: !systemConfig.multiTenantEnabled })}
              className={`w-14 h-7 rounded-full transition-all flex items-center px-1.5 shadow-inner ${
                systemConfig.multiTenantEnabled ? 'bg-[#0094c8]' : 'bg-slate-300'
              }`}
            >
              <div className={`w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-300 ${
                systemConfig.multiTenantEnabled ? 'translate-x-7' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {!systemConfig.multiTenantEnabled && (
            <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
              <div>
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Static Entry Region</h3>
                <p className="text-[10px] text-slate-500 font-medium mt-1 leading-relaxed">
                  Specify the region users will land on by default in single-tenant mode.
                </p>
              </div>
              <select
                value={systemConfig.selectedRegionId}
                onChange={(e) => updateSystemConfig({ selectedRegionId: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm font-bold bg-white text-[#011d3c] outline-none"
              >
                {[{ id: 'global', name: 'Global Network' }, ...tenants].map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
        </section>
        <div className="h-px bg-slate-100 w-full" />

        {/* Domain & Base URL Routing Section */}
        <section className="space-y-4">
          <div className="flex items-center space-x-2">
            <Globe className="w-4 h-4 text-[#0094c8]" />
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Portal Domains & Email Base URLs</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Global Default Base URL */}
            <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Global Default Base URL</h4>
                <p className="text-[10px] text-slate-500 font-medium mt-1 leading-relaxed">
                  Default domain for general notifications and all regions without a custom domain.
                </p>
              </div>
              <div className="space-y-2">
                <input
                  type="text"
                  value={globalBranding?.customDomain || ''}
                  onChange={(e) => updateGlobalBranding({ customDomain: e.target.value.trim() })}
                  placeholder="e.g. wblportal.com"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:ring-1 focus:ring-[#0094c8]"
                />
                <p className="text-[10px] text-[#0094c8] font-bold font-mono">
                  Active: {globalBranding?.customDomain ? (globalBranding.customDomain.startsWith('http') ? globalBranding.customDomain : `https://${globalBranding.customDomain}`) : 'https://wblportal.com'}
                </p>
              </div>
            </div>

            {/* Spartanburg Custom Domain */}
            {(() => {
              const spartanburg = tenants.find(t => t.id === 'spartanburg');
              return (
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                  <div>
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Spartanburg Region Portal</h4>
                    <p className="text-[10px] text-slate-500 font-medium mt-1 leading-relaxed">
                      Custom domain applied to all Spartanburg emails, invites, and notifications.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={spartanburg?.customDomain || ''}
                      onChange={(e) => updateTenantBranding('spartanburg', { customDomain: e.target.value.trim() })}
                      placeholder="e.g. thespartanburgedge.com"
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:ring-1 focus:ring-[#0094c8]"
                    />
                    <p className="text-[10px] text-emerald-600 font-bold font-mono">
                      Active: {spartanburg?.customDomain ? (spartanburg.customDomain.startsWith('http') ? spartanburg.customDomain : `https://${spartanburg.customDomain}`) : 'https://thespartanburgedge.com'}
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>
      </div>
    </div>
  );
};
